import { ActionIcon, Box, Button, Group, Modal, Table, Text } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconPencil, IconTrash } from '@tabler/icons-react';
import React, { useState } from 'react';

type Column<T> = {
  key: keyof T;
  header: string;
  align?: 'left' | 'right';
  renderValue?: (value: T[keyof T], item: T) => React.ReactNode;
};

interface TableViewProps<T> {
  items: T[];
  columns: Column<T>[];
  onEdit?: (item: T) => void;
  onDelete?: (item: T) => void;
}

export function TableViewBase<T>({ items, columns, onEdit, onDelete }: TableViewProps<T>) {
  const [deleteModalOpened, { open: openDeleteModal, close: closeDeleteModal }] =
    useDisclosure(false);
  const [itemToDelete, setItemToDelete] = useState<T | null>(null);

  const handleDeleteClick = (item: T) => {
    setItemToDelete(item);
    openDeleteModal();
  };

  const handleDeleteConfirm = () => {
    if (itemToDelete && onDelete) {
      onDelete(itemToDelete);
    }
    closeDeleteModal();
    setItemToDelete(null);
  };

  return (
    <>
      <Box>
        <Table withRowBorders={false} variant="cashflows-table">
          <Table.Thead>
            <Table.Tr>
              {columns.map(column => (
                <Table.Th key={String(column.key)} style={{ textAlign: column.align || 'left' }}>
                  {column.header}
                </Table.Th>
              ))}
              {(onEdit || onDelete) && (
                <Table.Th style={{ width: '100px', textAlign: 'right' }}></Table.Th>
              )}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {items.map((item, index) => (
              <Table.Tr key={index}>
                {columns.map(column => (
                  <Table.Td key={String(column.key)} style={{ textAlign: column.align || 'left' }}>
                    {column.renderValue
                      ? column.renderValue(item[column.key], item)
                      : String(item[column.key])}
                  </Table.Td>
                ))}
                {(onEdit || onDelete) && (
                  <Table.Td style={{ textAlign: 'right' }}>
                    <Group gap="xs" justify="flex-end">
                      {onEdit && (
                        <ActionIcon
                          variant="subtle"
                          color="gray.8"
                          onClick={() => onEdit(item)}
                          aria-label="Edit"
                        >
                          <IconPencil size={16} />
                        </ActionIcon>
                      )}
                      {onDelete && (
                        <ActionIcon
                          variant="subtle"
                          color="gray.8"
                          onClick={() => handleDeleteClick(item)}
                          aria-label="Delete"
                        >
                          <IconTrash size={16} />
                        </ActionIcon>
                      )}
                    </Group>
                  </Table.Td>
                )}
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Box>

      <Modal opened={deleteModalOpened} onClose={closeDeleteModal} title="Are you sure?" centered>
        <Text c="gray.10">Are you sure you want to remove this item?</Text>
        <Group justify="flex-end" mt="xl">
          <Button variant="pill" onClick={closeDeleteModal}>
            Cancel
          </Button>
          <Button variant="pill" color="red" onClick={handleDeleteConfirm}>
            Remove
          </Button>
        </Group>
      </Modal>
    </>
  );
}
